/* eslint-disable prettier/prettier */
import CloseIcon from '@mui/icons-material/Close'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { Box, Button, IconButton, Typography } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import { useDispatch, useSelector } from 'react-redux'
import { clearTemplateData } from '../features/templateSlice'
import { saveTemplate } from '../helpers/templateDownload'
import { SURFACE, TEXT } from '../theme/tokens'
import TemplateRenderer from './TemplateRenderer'

// Renders entirely from the template slice; clearTemplateData resets
// templateData, commType, previewId and showTemplatePreview together.
const TemplatePreview = () => {
    const dispatch = useDispatch()
    const {
        showTemplatePreview,
        commType,
        templateData,
        previewError,
        previewId,
        templates,
        pinpointTemplates,
    } = useSelector((state) => state.template)
    const close = () => dispatch(clearTemplateData())
    // A preview opened for an id the list does not hold (Comms Finder's
    // Open template) has no name to show; its download is named by the id.
    // Pinpoint's rows are listed beside the CommTemplates ones.
    const name = [...templates, ...(pinpointTemplates ?? [])].find(
        (t) => t.id === previewId
    )?.template_name
    // Saves the body as stored, not the sanitised HTML the preview renders.
    const download = () =>
        saveTemplate(templateData, { name: name || previewId, commType })

    return (
        <Dialog
            open={showTemplatePreview}
            // Esc and a backdrop click close it like the X does; without
            // onClose MUI ignores both and the only way out is the icon.
            onClose={close}
            aria-label="Template preview"
            sx={{
                overflow: commType === 'email' ? 'hidden' : 'scroll',
                '.MuiDialog-paper': { maxWidth: '100%' },
            }}
        >
            {/* A bar of its own on the dark surface: the close icon used to
                float over the email itself, grey on white (2.7:1). Sticky, so
                Download and Close stay in reach down a long email. */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pl: 2,
                    pr: 1,
                    py: 0.75,
                    background: SURFACE.card,
                    borderBottom: `1px solid ${SURFACE.border}`,
                }}
            >
                <Typography
                    title={name}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        typography: 'body2',
                        fontWeight: 600,
                        color: TEXT.primary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {name || 'Template preview'}
                </Typography>
                {!previewError && templateData && (
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadOutlinedIcon />}
                        onClick={download}
                    >
                        Download
                    </Button>
                )}
                <IconButton
                    aria-label="close"
                    onClick={close}
                    sx={{ color: TEXT.muted }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <TemplateRenderer />
        </Dialog>
    )
}

export default TemplatePreview
